const prefixToProperty: Record<string, string> = {
  // Display
  flex: 'display',
  block: 'display',
  inline: 'display',
  'inline-block': 'display',
  'inline-flex': 'display',
  hidden: 'display',
  grid: 'display',
  'inline-grid': 'display',

  // Grid layout
  'grid-cols': 'gridCols',

  // Flex layout
  'flex-row': 'flexDirection',
  'flex-row-reverse': 'flexDirection',
  'flex-col': 'flexDirection',
  'flex-col-reverse': 'flexDirection',
  'flex-wrap': 'flexWrap',
  'flex-wrap-reverse': 'flexWrap',
  'flex-nowrap': 'flexWrap',
  grow: 'flexGrow',
  shrink: 'flexShrink',
  basis: 'flexBasis',
  'items-start': 'alignItems',
  'items-end': 'alignItems',
  'items-center': 'alignItems',
  'items-baseline': 'alignItems',
  'items-stretch': 'alignItems',
  'justify-start': 'justifyContent',
  'justify-end': 'justifyContent',
  'justify-center': 'justifyContent',
  'justify-between': 'justifyContent',
  'justify-around': 'justifyContent',
  'justify-evenly': 'justifyContent',
  'justify-normal': 'justifyContent',
  'justify-stretch': 'justifyContent',

  // Gap
  gap: 'gap',
  'gap-x': 'gapX',
  'gap-y': 'gapY',

  // Sizing
  w: 'width',
  h: 'height',
  'min-w': 'minWidth',
  'max-w': 'maxWidth',
  'min-h': 'minHeight',
  'max-h': 'maxHeight',
  size: 'size',

  // Padding
  p: 'padding',
  px: 'paddingX',
  py: 'paddingY',
  pt: 'paddingTop',
  pb: 'paddingBottom',
  pl: 'paddingLeft',
  pr: 'paddingRight',

  // Margin
  m: 'margin',
  mx: 'marginX',
  my: 'marginY',
  mt: 'marginTop',
  mb: 'marginBottom',
  ml: 'marginLeft',
  mr: 'marginRight',

  // Typography - text alignment (full class matches)
  'text-left': 'textAlign',
  'text-center': 'textAlign',
  'text-right': 'textAlign',
  'text-justify': 'textAlign',

  // Typography - font size (prefix match)
  text: 'fontSize',

  // Typography - font (needs disambiguation between fontFamily and fontWeight)
  font: 'fontFamily',

  // Typography - font weight
  'font-thin': 'fontWeight',
  'font-extralight': 'fontWeight',
  'font-light': 'fontWeight',
  'font-normal': 'fontWeight',
  'font-medium': 'fontWeight',
  'font-semibold': 'fontWeight',
  'font-bold': 'fontWeight',
  'font-extrabold': 'fontWeight',
  'font-black': 'fontWeight',

  // Typography - line height
  leading: 'lineHeight',

  // Typography - letter spacing
  tracking: 'letterSpacing',

  // Typography - text transform
  uppercase: 'textTransform',
  lowercase: 'textTransform',
  capitalize: 'textTransform',
  'normal-case': 'textTransform',

  // Typography - font style
  italic: 'fontStyle',
  'not-italic': 'fontStyle',

  // Typography - text decoration
  underline: 'textDecoration',
  'line-through': 'textDecoration',
  'no-underline': 'textDecoration',

  // Colors (prefix matches, value contains color)
  bg: 'backgroundColor',
  'hover:bg': 'hoverBackgroundColor',
  'hover:text': 'hoverTextColor',

  // Text color uses same 'text' prefix as fontSize - needs disambiguation
  // handled via resolveAmbiguousPrefix for color patterns

  // Border
  border: 'borderWidth',
  'border-solid': 'borderStyle',
  'border-dashed': 'borderStyle',
  'border-dotted': 'borderStyle',
  'border-double': 'borderStyle',
  'border-none': 'borderStyle',
  rounded: 'borderRadius',

  // Effects
  opacity: 'opacity',
  shadow: 'shadow',
  'inset-shadow': 'shadow',

  // Overflow
  overflow: 'overflow',
  'overflow-x': 'overflow',
  'overflow-y': 'overflow',

  // Object fit
  'object-contain': 'objectFit',
  'object-cover': 'objectFit',
  'object-fill': 'objectFit',
  'object-none': 'objectFit',
  'object-scale-down': 'objectFit',

  // Aspect ratio
  aspect: 'aspectRatio',

  // Background position
  'bg-top-left': 'backgroundPosition',
  'bg-top': 'backgroundPosition',
  'bg-top-right': 'backgroundPosition',
  'bg-left': 'backgroundPosition',
  'bg-center': 'backgroundPosition',
  'bg-right': 'backgroundPosition',
  'bg-bottom-left': 'backgroundPosition',
  'bg-bottom': 'backgroundPosition',
  'bg-bottom-right': 'backgroundPosition',

  // Background size
  'bg-auto': 'backgroundSize',
  'bg-cover': 'backgroundSize',
  'bg-contain': 'backgroundSize',

  // Transitions
  transition: 'transition',
  'transition-none': 'transition',
  'transition-colors': 'transition',
  duration: 'transitionDuration',

  // SVG
  stroke: 'strokeWidth',
};

/**
 * Maps standalone compound classes to their semantic values.
 * For classes like 'border-none', we want to return 'none' not 'border-none'.
 * Classes not in this map return their full class name as the value.
 */
const standaloneClassValues: Record<string, string> = {
  // Border style
  'border-solid': 'solid',
  'border-dashed': 'dashed',
  'border-dotted': 'dotted',
  'border-double': 'double',
  'border-none': 'none',

  // Object fit
  'object-contain': 'contain',
  'object-cover': 'cover',
  'object-fill': 'fill',
  'object-none': 'none',
  'object-scale-down': 'scale-down',

  // Flex direction
  'flex-row': 'row',
  'flex-row-reverse': 'row-reverse',
  'flex-col': 'column',
  'flex-col-reverse': 'column-reverse',

  // Flex wrap
  'flex-wrap': 'wrap',
  'flex-wrap-reverse': 'wrap-reverse',
  'flex-nowrap': 'nowrap',

  // Align items
  'items-start': 'flex-start',
  'items-end': 'flex-end',
  'items-center': 'center',
  'items-baseline': 'baseline',
  'items-stretch': 'stretch',

  // Justify content
  'justify-start': 'flex-start',
  'justify-end': 'flex-end',
  'justify-center': 'center',
  'justify-between': 'space-between',
  'justify-around': 'space-around',
  'justify-evenly': 'space-evenly',
  'justify-normal': 'normal',
  'justify-stretch': 'stretch',

  // Text align
  'text-left': 'left',
  'text-center': 'center',
  'text-right': 'right',
  'text-justify': 'justify',

  // Text transform
  'normal-case': 'none',

  // Font style
  'not-italic': 'normal',

  // Text decoration
  'line-through': 'line-through',
  'no-underline': 'none',

  // Background position
  'bg-top-left': 'top left',
  'bg-top': 'top',
  'bg-top-right': 'top right',
  'bg-left': 'left',
  'bg-center': 'center',
  'bg-right': 'right',
  'bg-bottom-left': 'bottom left',
  'bg-bottom': 'bottom',
  'bg-bottom-right': 'bottom right',

  // Background size
  'bg-auto': 'auto',
  'bg-cover': 'cover',
  'bg-contain': 'contain',

  // Transition
  'transition-none': 'none',
  'transition-colors': 'colors',
};

/**
 * Get the semantic value for a standalone class.
 * Returns the mapped value if it exists, otherwise returns the class name itself.
 */
export function getStandaloneClassValue(className: string): string {
  return standaloneClassValues[className] ?? className;
}

const spacingValues: Record<string, string> = {
  '0': '0px',
  px: '1px',
  '0.5': '2px',
  '1': '4px',
  '1.5': '6px',
  '2': '8px',
  '2.5': '10px',
  '3': '12px',
  '3.5': '14px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '7': '28px',
  '8': '32px',
  '9': '36px',
  '10': '40px',
  '11': '44px',
  '12': '48px',
  '14': '56px',
  '16': '64px',
  '20': '80px',
  '24': '96px',
  '28': '112px',
  '32': '128px',
  '36': '144px',
  '40': '160px',
  '44': '176px',
  '48': '192px',
  '52': '208px',
  '56': '224px',
  '60': '240px',
  '64': '256px',
  '72': '288px',
  '80': '320px',
  '96': '384px',
};

const fontSizeValues: Record<string, string> = {
  xs: '12px',
  sm: '14px',
  base: '16px',
  lg: '18px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '30px',
  '4xl': '36px',
  '5xl': '48px',
  '6xl': '60px',
  '7xl': '72px',
  '8xl': '96px',
  '9xl': '128px',
};

const borderRadiusValues: Record<string, string> = {
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  '2xl': '16px',
  '3xl': '24px',
  '4xl': '32px',
  full: '9999px',
};

const letterSpacingValues: Record<string, string> = {
  tighter: '-0.8px',
  tight: '-0.4px',
  normal: '0px',
  wide: '0.4px',
  wider: '0.8px',
  widest: '1.6px',
};

/**
 * Maps Tailwind font weight names to their numeric CSS values.
 * @example FONT_WEIGHT_MAP['bold'] // '700'
 */
export const FONT_WEIGHT_MAP: Record<string, string> = {
  thin: '100',
  extralight: '200',
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  extrabold: '800',
  black: '900',
};

const maxWidthValues: Record<string, string> = {
  '3xs': '256px',
  '2xs': '288px',
  xs: '320px',
  sm: '384px',
  md: '448px',
  lg: '512px',
  xl: '576px',
  '2xl': '672px',
  '3xl': '768px',
  '4xl': '896px',
  '5xl': '1024px',
  '6xl': '1152px',
  '7xl': '1280px',
};

export function getPropertyForPrefix(prefix: string): string | undefined {
  return prefixToProperty[prefix];
}

export function getNormalizedValue(prefix: string, value: string): string | undefined {
  const property = getPropertyForPrefix(prefix);

  if (!property) {
    return undefined;
  }

  if (property === 'fontSize') {
    return fontSizeValues[value];
  }

  if (property === 'borderRadius') {
    return borderRadiusValues[value];
  }

  if (property === 'letterSpacing') {
    return letterSpacingValues[value];
  }

  if (property === 'fontWeight') {
    return FONT_WEIGHT_MAP[value];
  }

  if (property === 'maxWidth') {
    // Try named sizes first (3xs, 2xs, xs, sm, etc.), then fall back to spacing
    return maxWidthValues[value] ?? spacingValues[value];
  }

  // Width and height can use named sizes (xs, sm, md, etc.) or spacing values
  if (property === 'width' || property === 'minWidth') {
    return maxWidthValues[value] ?? spacingValues[value];
  }

  if (
    property.toLowerCase().includes('padding') ||
    property.toLowerCase().includes('margin') ||
    property.toLowerCase().includes('gap') ||
    property.toLowerCase().includes('height') ||
    property === 'size'
  ) {
    return spacingValues[value];
  }

  return undefined;
}

export function getAllPrefixes(): string[] {
  return Object.keys(prefixToProperty);
}

export function isAmbiguousPrefix(prefix: string): boolean {
  return prefix === 'text' || prefix === 'bg' || prefix === 'border' || prefix === 'font';
}

/**
 * All supported property names for Tailwind class parsing.
 * These are the canonical property identifiers used throughout the system.
 */
export const TAILWIND_PROPERTIES = [
  // Typography
  'fontSize',
  'fontFamily',
  'fontWeight',
  'lineHeight',
  'letterSpacing',
  'textAlign',
  'textTransform',
  'fontStyle',
  'textDecoration',
  'textColor',
  'hoverTextColor',

  // Spacing - Padding
  'padding',
  'paddingX',
  'paddingY',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',

  // Spacing - Margin
  'margin',
  'marginX',
  'marginY',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',

  // Gap
  'gap',
  'gapX',
  'gapY',

  // Sizing
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'size',

  // Layout
  'display',
  'flex',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'flexDirection',
  'alignItems',
  'justifyContent',
  'flexWrap',
  'gridCols',
  'objectFit',
  'overflow',
  'aspectRatio',

  // Border
  'borderRadius',
  'borderWidth',
  'borderStyle',
  'borderColor',

  // Colors
  'backgroundColor',
  'hoverBackgroundColor',

  // Background
  'backgroundPosition',
  'backgroundSize',

  // Effects
  'opacity',
  'shadow',

  // Transitions
  'transition',
  'transitionDuration',

  // Other
  'strokeWidth',
] as const;

export type TailwindProperty = (typeof TAILWIND_PROPERTIES)[number];

/**
 * Mapping from CSS property names to their Tailwind prefix.
 * Includes both simple prefix-value patterns and enumerated value properties.
 * For enumerated values (display, flexWrap, etc.), the prefix may be empty
 * because they use full class names.
 */
const propertyToPrefix: Record<string, string> = {
  // Typography
  fontSize: 'text',
  fontFamily: 'font',
  fontWeight: 'font',
  lineHeight: 'leading',
  letterSpacing: 'tracking',
  textAlign: 'text',
  textTransform: '',
  fontStyle: '',
  textDecoration: '',

  // Spacing
  padding: 'p',
  paddingX: 'px',
  paddingY: 'py',
  paddingTop: 'pt',
  paddingBottom: 'pb',
  paddingLeft: 'pl',
  paddingRight: 'pr',
  margin: 'm',
  marginX: 'mx',
  marginY: 'my',
  marginTop: 'mt',
  marginBottom: 'mb',
  marginLeft: 'ml',
  marginRight: 'mr',
  gap: 'gap',
  gapX: 'gap-x',
  gapY: 'gap-y',
  gridCols: 'grid-cols',

  // Sizing
  width: 'w',
  height: 'h',
  minWidth: 'min-w',
  maxWidth: 'max-w',
  minHeight: 'min-h',
  maxHeight: 'max-h',
  size: 'size',

  // Layout
  display: '',
  flex: 'flex',
  flexGrow: 'grow',
  flexShrink: 'shrink',
  flexBasis: 'basis',
  flexDirection: 'flex',
  alignItems: 'items',
  justifyContent: 'justify',
  flexWrap: 'flex',
  objectFit: 'object',
  overflow: 'overflow',

  // Border
  borderRadius: 'rounded',
  borderWidth: 'border',
  borderStyle: 'border',
  borderColor: 'border',

  // Colors
  backgroundColor: 'bg',
  textColor: 'text',
  hoverBackgroundColor: 'hover:bg',
  hoverTextColor: 'hover:text',

  // Background
  backgroundPosition: 'bg-position',
  backgroundSize: 'bg',

  // Effects
  opacity: 'opacity',
  shadow: 'shadow',

  // Other
  aspectRatio: 'aspect',
  transitionDuration: 'duration',
  strokeWidth: 'stroke',
};

export function getPrefixForProperty(property: string): string | undefined {
  return propertyToPrefix[property];
}

// Known font size values - anything else with text- prefix is a color
const FONT_SIZE_VALUES = new Set([
  'xs',
  'sm',
  'base',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl',
  '8xl',
  '9xl',
]);

// Known border width values - anything else with border- prefix (that's not a style) is a color
const BORDER_WIDTH_VALUES = new Set(['0', '1', '2', '4', '8']);

export function resolveAmbiguousPrefix(prefix: string, fullClass: string): string | undefined {
  if (prefix === 'flex') {
    if (fullClass === 'flex') {
      return 'display';
    }
    if (
      fullClass === 'flex-1' ||
      fullClass === 'flex-auto' ||
      fullClass === 'flex-initial' ||
      fullClass === 'flex-none'
    ) {
      return 'flex';
    }
  }

  if (prefix === 'text') {
    // Text alignment
    if (
      fullClass.startsWith('text-left') ||
      fullClass.startsWith('text-center') ||
      fullClass.startsWith('text-right') ||
      fullClass.startsWith('text-justify')
    ) {
      return 'textAlign';
    }

    // Extract the value after 'text-'
    const value = fullClass.slice(5); // Remove 'text-'

    // Font size if it's a known size value or arbitrary size like [18px]
    if (FONT_SIZE_VALUES.has(value) || /^\[\d+(\.\d+)?(px|rem|em)?\]$/.test(value)) {
      return 'fontSize';
    }

    // Everything else is textColor (theme colors like text-primary, custom colors like text-[#ff0000])
    return 'textColor';
  }

  if (prefix === 'bg') {
    // Background position
    if (
      fullClass === 'bg-top-left' ||
      fullClass === 'bg-top' ||
      fullClass === 'bg-top-right' ||
      fullClass === 'bg-left' ||
      fullClass === 'bg-center' ||
      fullClass === 'bg-right' ||
      fullClass === 'bg-bottom-left' ||
      fullClass === 'bg-bottom' ||
      fullClass === 'bg-bottom-right'
    ) {
      return 'backgroundPosition';
    }
    // Background size
    if (fullClass === 'bg-auto' || fullClass === 'bg-cover' || fullClass === 'bg-contain') {
      return 'backgroundSize';
    }
    // Default to backgroundColor (theme colors like bg-primary, custom colors like bg-[#ff0000])
    return 'backgroundColor';
  }

  if (prefix === 'border') {
    // Border style
    if (
      fullClass === 'border-solid' ||
      fullClass === 'border-dashed' ||
      fullClass === 'border-dotted' ||
      fullClass === 'border-double' ||
      fullClass === 'border-none'
    ) {
      return 'borderStyle';
    }

    // Extract the value after 'border-'
    const value = fullClass.slice(7); // Remove 'border-'

    // Border width if it's a known width value or arbitrary width like [2px]
    if (BORDER_WIDTH_VALUES.has(value) || /^\[\d+(px)?\]$/.test(value)) {
      return 'borderWidth';
    }

    // Everything else is borderColor (theme colors like border-primary, custom colors)
    return 'borderColor';
  }

  if (prefix === 'font') {
    // Font weight classes (named weights)
    if (
      fullClass === 'font-thin' ||
      fullClass === 'font-extralight' ||
      fullClass === 'font-light' ||
      fullClass === 'font-normal' ||
      fullClass === 'font-medium' ||
      fullClass === 'font-semibold' ||
      fullClass === 'font-bold' ||
      fullClass === 'font-extrabold' ||
      fullClass === 'font-black'
    ) {
      return 'fontWeight';
    }
    // Arbitrary font weights (font-[300], font-[400], etc.)
    if (/^font-\[\d+\]$/.test(fullClass)) {
      return 'fontWeight';
    }
    // Default to fontFamily (font-sans, font-serif, font-[custom])
    return 'fontFamily';
  }

  return getPropertyForPrefix(prefix);
}

/**
 * Maps property names to their CSS property names for use with CSS.supports().
 * Handles shorthand properties (paddingX -> padding-left) and hover variants.
 */
const propertyToCssProperty: Record<string, string> = {
  paddingX: 'padding-left',
  paddingY: 'padding-top',
  paddingTop: 'padding-top',
  paddingBottom: 'padding-bottom',
  paddingLeft: 'padding-left',
  paddingRight: 'padding-right',
  marginX: 'margin-left',
  marginY: 'margin-top',
  marginTop: 'margin-top',
  marginBottom: 'margin-bottom',
  marginLeft: 'margin-left',
  marginRight: 'margin-right',
  backgroundColor: 'background-color',
  hoverBackgroundColor: 'background-color',
  backgroundPosition: 'background-position',
  backgroundSize: 'background-size',
  borderColor: 'border-color',
  borderStyle: 'border-style',
  borderWidth: 'border-width',
  borderRadius: 'border-radius',
  flex: 'flex',
  flexGrow: 'flex-grow',
  flexShrink: 'flex-shrink',
  flexBasis: 'flex-basis',
  flexDirection: 'flex-direction',
  alignItems: 'align-items',
  justifyContent: 'justify-content',
  flexWrap: 'flex-wrap',
  gap: 'gap',
  minWidth: 'min-width',
  maxWidth: 'max-width',
  maxHeight: 'max-height',
  minHeight: 'min-height',
  width: 'width',
  height: 'height',
  objectFit: 'object-fit',
  aspectRatio: 'aspect-ratio',
  opacity: 'opacity',
  shadow: 'box-shadow',
  overflow: 'overflow',
  textColor: 'color',
  hoverTextColor: 'color',
  fontFamily: 'font-family',
  fontWeight: 'font-weight',
  fontSize: 'font-size',
  lineHeight: 'line-height',
  letterSpacing: 'letter-spacing',
  textAlign: 'text-align',
  textTransform: 'text-transform',
  fontStyle: 'font-style',
  textDecoration: 'text-decoration',
  size: 'width',
  strokeWidth: 'stroke-width',
  display: 'display',
  gridCols: 'grid-template-columns',
};

/**
 * Convert a Tailwind property name to its CSS property equivalent.
 * Useful for CSS.supports() validation.
 *
 * @example
 * tailwindToCssProperty('fontSize');      // 'font-size'
 * tailwindToCssProperty('paddingX');      // 'padding-left'
 * tailwindToCssProperty('backgroundColor'); // 'background-color'
 */
export function tailwindToCssProperty(property: string): string | null {
  return propertyToCssProperty[property] ?? null;
}
